import { useState } from "react";
import { Share2 } from "lucide-react";
import { Icon, UrlBar } from "@cntrl/ui";
import { pushToast } from "../../app/toasts.js";
import styles from "./InviteLinkShare.module.scss";

export interface InviteLinkShareProps {
  /** The absolute `/join?token=…` link — server-built, because the wrapper's own origin is not the public one. */
  url: string;
  /** When the link stops working (ISO). */
  expiresOn?: string | null;
  /** Who it is for, for the sentence above it ("Send this link to dana@example.com"). */
  recipient?: string | null;
  /** True on a FRESH invite — the server has also emailed it, and saying so stops a second send. */
  emailed?: boolean;
}

/** "Oct 15" — the expiry, wall-clock local. */
function fmtDay(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * An invite's `/join` link, ready to send (ART-239) — the one surface every seat invite ends on: a new crew
 * member, an org teammate, a partner. Copy always; Share where the platform has a share sheet (the iOS wrapper and
 * phone browsers), which is the natural way to text a link to somebody.
 *
 * It exists because, until it did, an invite minted a link and threw it away: nothing was emailed, nothing was
 * shown, and the person could only ever be let in by someone who already had an account.
 */
export function InviteLinkShare({ url, expiresOn, recipient, emailed = false }: InviteLinkShareProps) {
  const [copied, setCopied] = useState(false);
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const day = fmtDay(expiresOn);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      pushToast("Couldn't copy the link. Select it and copy it yourself.");
    }
  }

  async function share() {
    try {
      await navigator.share({ url });
    } catch {
      // Dismissing the share sheet rejects too — not an error worth a toast.
    }
  }

  return (
    <div className={styles.share}>
      <p className={styles.note}>
        {/* A guest-list name often ends in an initial's dot ("Jo P.") — never print it twice. */}
        {recipient ? <>Send this link to <b>{recipient}</b>{recipient.endsWith(".") ? "" : "."}</> : "Send this link."}{" "}
        It works once{day ? ` and expires ${day}` : ""}.{emailed ? " We've emailed it too." : ""}
      </p>
      <UrlBar
        url={url}
        onCopy={() => void copy()}
        copyLabel={copied ? "Copied" : "Copy"}
        preview={false}
        stack
        actions={
          canShare ? (
            <button type="button" className={styles.shareBtn} onClick={() => void share()}>
              <Icon icon={Share2} size={16} /> Share
            </button>
          ) : undefined
        }
      />
    </div>
  );
}
